const DESCRIPTION_LIMIT = 4000;

const toolbarOptions = [
  ["bold", "italic"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link"],
  ["undo"],
  ["redo"],
  [{ direction: "rtl" }],
  ["clean"],
];

/**
 * Registers the same Quill controls used by the audio-only episode form.
 *
 * @param {Function} Quill Quill constructor from the page.
 * @returns {void}
 */
function registerAudioDescriptionControls(Quill) {
  if (!Quill || Quill.groupedAudioDescriptionControlsRegistered) return;

  const icons = Quill.import("ui/icons");
  icons.undo = `<svg viewbox="0 0 18 18">
    <polygon class="ql-fill ql-stroke" points="6 10 4 12 2 10 6 10"></polygon>
    <path class="ql-stroke" d="M8.09,13.91A4.6,4.6,0,0,0,9,14,5,5,0,1,0,4,9"></path>
  </svg>`;
  icons.redo = `<svg viewbox="0 0 18 18">
    <polygon class="ql-fill ql-stroke" points="12 10 14 12 16 10 12 10"></polygon>
    <path class="ql-stroke" d="M9.91,13.91A4.6,4.6,0,0,1,9,14a5,5,0,1,1,5-5"></path>
  </svg>`;

  Quill.register("modules/counter", (quill, options) => {
    quill.on("text-change", () => updateDescriptionCounter(options.container, quill.getText()));
  });
  Quill.groupedAudioDescriptionControlsRegistered = true;
}

/**
 * Builds toolbar handlers that match the audio-only description editor.
 *
 * @returns {Object} Quill toolbar handlers.
 */
function toolbarHandlers() {
  return {
    undo() {
      this.quill.history.undo();
    },
    redo() {
      this.quill.history.redo();
    },
    link(value) {
      if (value) {
        const href = prompt("Enter the URL", "https://");
        this.quill.format("link", href);
      } else {
        this.quill.format("link", false);
      }
    },
  };
}

/**
 * Updates the grouped editor's word and character count.
 *
 * @param {HTMLElement} counterElement element displaying the count.
 * @param {string} text unformatted editor text.
 * @returns {void}
 */
export function updateDescriptionCounter(counterElement, text) {
  if (!counterElement) return;

  const characters = Math.max(text.length - 1, 0);
  const words = Math.max(text.split(/\s+/).length - 1, 0);

  if (characters > DESCRIPTION_LIMIT) {
    counterElement.innerHTML = `<span class="text-red-400 font-bold">Reached limit of ${characters} characters for Apple description cutoff</span>`;
  } else if (characters === 0) {
    counterElement.textContent = "0 words, 0 characters";
  } else {
    counterElement.textContent = `${words} words, ${characters} characters`;
  }
}

/**
 * Wires the grouped YouTube publishing controls without relying on Alpine.
 *
 * @param {Document|HTMLElement} root document or subtree to initialize.
 * @returns {void}
 */
export function initializeGroupedYoutubePublishing(root = document) {
  root.querySelectorAll("[data-grouped-youtube-publishing]").forEach((container) => {
    const actionSelect = container.querySelector("[data-youtube-publishing-action]");
    if (!actionSelect) return;

    const schedulePanel = container.querySelector("[data-youtube-schedule-panel]");
    const scheduleInput = container.querySelector("[data-youtube-schedule-input]");
    const visibilityPanel = container.querySelector("[data-youtube-visibility-panel]");
    const publishModeInput = container.querySelector("[data-youtube-publish-mode]");
    const privateVisibility = container.querySelector("[data-youtube-visibility-private]");

    const syncPublishingControls = () => {
      const action = actionSelect.value;
      const isScheduled = action === "schedule";
      const isPublishNow = action === "publish";

      actionSelect.classList.remove("pointer-events-none", "text-gray-400");
      actionSelect.removeAttribute("aria-disabled");
      schedulePanel?.classList.toggle("hidden", !isScheduled);
      visibilityPanel?.classList.toggle("hidden", !isPublishNow);

      if (scheduleInput) {
        scheduleInput.required = isScheduled;
      }
      if (publishModeInput) publishModeInput.value = isScheduled ? "scheduled" : "publish_now";

      // YouTube requires scheduled videos to be private until the scheduled time.
      if (isScheduled && privateVisibility) privateVisibility.checked = true;
    };

    actionSelect.onchange = syncPublishingControls;
    syncPublishingControls();
  });
}

function clearGroupedValidationErrors(form) {
  form.querySelectorAll('[data-grouped-validation-error]').forEach((error) => {
    error.textContent = '';
    error.classList.add('hidden');
  });
}

function showGroupedValidationError(form, key, message, target) {
  const error = form.querySelector(`[data-grouped-validation-error="${key}"]`);
  if (error) {
    error.textContent = message;
    error.classList.remove('hidden');
  }
  const platformDetails = target?.closest?.('details');
  if (platformDetails) platformDetails.open = true;
  target?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  target?.focus?.({ preventScroll: true });
  return false;
}

function groupedDescriptionBlank(value) {
  const description = value?.trim() || '';
  return description === '' || description === '<p><br></p>';
}

/**
 * Validates fields needed for an actual grouped provider submission.
 * Draft and navigation actions deliberately bypass these checks.
 *
 * @param {HTMLFormElement} form grouped video form.
 * @returns {boolean} whether the form can be submitted.
 */
export function validateGroupedVideoSubmission(form) {
  clearGroupedValidationErrors(form);

  const source = form.querySelector('[data-video-source-upload]');
  if (source?.dataset.videoSourceReady !== 'true') {
    return showGroupedValidationError(form, 'video_source', 'Please upload a video file.', source);
  }

  const title = form.querySelector('input[name="video_episode[title]"]');
  if (!title?.value?.trim()) {
    return showGroupedValidationError(form, 'title', 'Please enter a title for your video.', title);
  }

  const description = form.querySelector('[data-grouped-video-description-input]');
  if (groupedDescriptionBlank(description?.value)) {
    const descriptionEditor = form.querySelector('[data-grouped-video-description-editor]');
    return showGroupedValidationError(
      form,
      'description',
      'Please enter a description for your video.',
      descriptionEditor
    );
  }

  const appleSelected = form.dataset.groupedAppleSelected === 'true';
  const youtubeSelected = form.dataset.groupedYoutubeSelected === 'true';
  const youtubeAction = form.querySelector('[data-youtube-publishing-action]')?.value;

  const category = form.querySelector('#video_episode_category');
  if ((appleSelected || (youtubeSelected && youtubeAction !== 'draft')) && !category?.value) {
    return showGroupedValidationError(
      form,
      'category',
      'Please select a category for your video.',
      category
    );
  }

  if (!youtubeSelected || youtubeAction === 'draft') return true;

  const playlistContainer = form.querySelector('[data-grouped-youtube-playlists]');
  if (form.dataset.groupedYoutubeConnected !== 'true') {
    return showGroupedValidationError(
      form,
      'youtube_playlist_id',
      'Connect YouTube in Distribution settings before publishing.',
      playlistContainer
    );
  }

  const playlist = form.querySelector('[data-grouped-youtube-playlist-id]');
  if (!playlist?.value) {
    return showGroupedValidationError(
      form,
      'youtube_playlist_id',
      'Please select a playlist for your YouTube video.',
      playlistContainer
    );
  }

  const visibility = form.querySelector('input[name="app_youtube_episode[visibility_settings]"]:checked');
  const visibilityPanel = form.querySelector('[data-youtube-visibility-panel]');
  if (!visibility) {
    return showGroupedValidationError(
      form,
      'visibility_settings',
      'Please select a visibility setting for your YouTube video.',
      visibilityPanel
    );
  }

  if (youtubeAction === 'schedule') {
    const schedule = form.querySelector('[data-youtube-schedule-input]');
    if (!schedule?.value) {
      return showGroupedValidationError(
        form,
        'scheduled_at_time',
        'Please select a date to schedule your episode.',
        schedule
      );
    }
    if (visibility.value !== 'private') {
      return showGroupedValidationError(
        form,
        'visibility_settings',
        'Scheduled videos must be set to Private.',
        visibilityPanel
      );
    }
  }

  const kids = form.querySelector('input[name="video_episode[content_made_for_kids]"]:checked');
  const kidsFieldset = form.querySelector('[data-grouped-video-kids]');
  if (!kids) {
    return showGroupedValidationError(
      form,
      'content_made_for_kids',
      'Please indicate if this content is made for kids (YouTube requirement).',
      kidsFieldset
    );
  }

  return true;
}

/**
 * Disables grouped form action buttons after a real submit starts.
 *
 * @param {Document|HTMLElement} root document or subtree to initialize.
 * @returns {void}
 */
export function initializeGroupedVideoSubmission(root = document) {
  root.querySelectorAll("[data-grouped-video-form]").forEach((form) => {
    if (form.groupedVideoSubmissionInitialized) return;
    form.groupedVideoSubmissionInitialized = true;

    form.addEventListener("submit", (event) => {
      if (form.dataset.submitting === "true") {
        event.preventDefault();
        return;
      }

      const submitter = event.submitter;
      const validatesProviderSubmission = submitter?.value === 'submit' ||
        submitter?.dataset.validatesGroupedSubmission === 'true';
      if (validatesProviderSubmission && !validateGroupedVideoSubmission(form)) {
        event.preventDefault();
        return;
      }

      form.dataset.submitting = "true";
      const loadingLabel = submitter?.dataset.loadingLabel || "Saving...";

      setTimeout(() => {
        form.querySelectorAll("[data-grouped-action-button]").forEach((button) => {
          button.disabled = true;
          button.classList.add("cursor-not-allowed", "border-gray-400", "bg-gray-400", "text-white");
          button.classList.remove("border-quill-active", "bg-quill-active", "text-quill-active");
        });

        if (submitter) submitter.textContent = loadingLabel;
      }, 0);
    });
  });
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
}

function setInlineError(element, message) {
  if (!element) return;

  element.textContent = message || "";
  element.classList.toggle("hidden", !message);
}

function addPlaylistOption(select, playlist) {
  if (!playlist?.id) return;

  const existing = Array.from(select.options || []).find((option) => option.value === playlist.id);
  if (existing) {
    existing.textContent = playlist.title || playlist.id;
    return;
  }

  const option = document.createElement("option");
  option.value = playlist.id;
  option.textContent = playlist.title || playlist.id;
  const createOption = select.querySelector('option[value="__create__"]');
  select.insertBefore(option, createOption);
}

function updateHiddenPlaylistFields({ select, playlists, playlistIdInput, playlistInfoInput }) {
  const playlist = playlists.find((item) => item.id === select.value);
  if (playlistIdInput) playlistIdInput.value = select.value === "__create__" ? "" : select.value;
  if (playlistInfoInput) playlistInfoInput.value = playlist ? JSON.stringify(playlist) : "";
}

function renderPlaylistOptions({ select, playlists, selectedPlaylistId, playlistIdInput, playlistInfoInput }) {
  playlists.forEach((playlist) => addPlaylistOption(select, playlist));
  if (selectedPlaylistId) select.value = selectedPlaylistId;
  updateHiddenPlaylistFields({ select, playlists, playlistIdInput, playlistInfoInput });
}

function closePlaylistModal(modal, createError) {
  modal?.classList.add("hidden");
  setInlineError(createError, "");
}

function bindPlaylistModalActions({
  container,
  modal,
  select,
  playlists,
  playlistIdInput,
  playlistInfoInput,
  createPlaylistUrl,
}) {
  const nameInput = modal?.querySelector("[data-grouped-youtube-playlist-name]");
  const descriptionInput = modal?.querySelector("[data-grouped-youtube-playlist-description]");
  const privacyInput = modal?.querySelector("[data-grouped-youtube-playlist-privacy]");
  const createError = modal?.querySelector("[data-grouped-youtube-playlist-create-error]");
  const createButton = modal?.querySelector("[data-grouped-youtube-playlist-create]");
  const cancelButtons = Array.from(modal?.querySelectorAll("[data-grouped-youtube-playlist-cancel]") || []);
  const copyButton = modal?.querySelector("[data-grouped-youtube-copy-show]");

  cancelButtons.forEach((cancelButton) => {
    cancelButton.onclick = () => closePlaylistModal(modal, createError);
  });

  if (copyButton) copyButton.onclick = () => {
    if (nameInput) nameInput.value = container.dataset.showTitle || "";
    if (descriptionInput) descriptionInput.value = container.dataset.showDescription || "";
    setInlineError(createError, "");
  };

  if (!createButton) return;

  createButton.onclick = async () => {
    setInlineError(createError, "");
    const title = nameInput?.value?.trim();
    const description = descriptionInput?.value?.trim();
    if (!title) return setInlineError(createError, "Playlist name is required.");
    if (!description) return setInlineError(createError, "Playlist description is required.");
    if (!createPlaylistUrl) return setInlineError(createError, "YouTube playlist creation is unavailable.");

    createButton.disabled = true;
    createButton.textContent = "Creating...";
    try {
      const response = await fetch(createPlaylistUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({
          title,
          description,
          privacy_status: privacyInput?.value || "private",
          use_show_cover_art: "true",
        }),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.error || "Failed to create playlist.");

      playlists.push(data.playlist);
      addPlaylistOption(select, data.playlist);
      select.value = data.playlist.id;
      updateHiddenPlaylistFields({ select, playlists, playlistIdInput, playlistInfoInput });
      closePlaylistModal(modal, createError);
      if (nameInput) nameInput.value = "";
      if (descriptionInput) descriptionInput.value = "";
    } catch (errorMessage) {
      setInlineError(createError, errorMessage.message || "Failed to create playlist.");
    } finally {
      createButton.disabled = false;
      createButton.textContent = "Create";
    }
  };
}

function loadGroupedYoutubePlaylists({
  container,
  playlistsUrl,
  playlists,
  select,
  selectedPlaylistId,
  playlistIdInput,
  playlistInfoInput,
  loading,
  help,
  error,
}) {
  if (!playlistsUrl) {
    setInlineError(error, "YouTube not connected.");
    return;
  }

  loading?.classList.remove("hidden");
  help?.classList.add("hidden");
  fetch(playlistsUrl, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
    },
  })
    .then((response) => response.json())
    .then((data) => {
      if (!data.success) throw new Error(data.error || "Failed to load playlists.");
      const currentSelection = playlists.find((playlist) => playlist.id === select.value);
      const fetchedPlaylists = data.playlists || [];
      if (currentSelection && !fetchedPlaylists.find((playlist) => playlist.id === currentSelection.id)) {
        fetchedPlaylists.unshift(currentSelection);
      }
      playlists.splice(0, playlists.length, ...fetchedPlaylists);
      if (selectedPlaylistId && !playlists.find((playlist) => playlist.id === selectedPlaylistId)) {
        playlists.unshift({
          id: selectedPlaylistId,
          title: container.dataset.selectedPlaylistTitle || selectedPlaylistId,
        });
      }
      renderPlaylistOptions({ select, playlists, selectedPlaylistId, playlistIdInput, playlistInfoInput });
    })
    .catch((errorMessage) => setInlineError(error, errorMessage.message || "Failed to load playlists."))
    .finally(() => {
      loading?.classList.add("hidden");
      help?.classList.remove("hidden");
    });
}

/**
 * Wires the grouped YouTube playlist selector to the same playlist APIs used by
 * the YouTube-only editor.
 *
 * @param {Document|HTMLElement} root document or subtree to initialize.
 * @returns {void}
 */
export function initializeGroupedYoutubePlaylists(root = document) {
  root.querySelectorAll("[data-grouped-youtube-playlists]").forEach((container) => {
    if (container.groupedYoutubePlaylistsInitialized) return;
    container.groupedYoutubePlaylistsInitialized = true;

    const select = container.querySelector("[data-grouped-youtube-playlist-select]");
    const playlistIdInput = root.querySelector("[data-grouped-youtube-playlist-id]");
    const playlistInfoInput = root.querySelector("[data-grouped-youtube-playlist-info]");
    const loading = container.querySelector("[data-grouped-youtube-playlists-loading]");
    const help = container.querySelector("[data-grouped-youtube-playlists-help]");
    const error = container.querySelector("[data-grouped-youtube-playlists-error]");
    const modal = root.querySelector("[data-grouped-youtube-playlist-modal]");
    const playlistsUrl = container.dataset.playlistApiUrl;
    const createPlaylistUrl = container.dataset.createPlaylistApiUrl;
    let playlists = [];

    if (!select) return;

    const selectedPlaylistId = container.dataset.selectedPlaylistId;
    if (selectedPlaylistId) {
      playlists.push({
        id: selectedPlaylistId,
        title: container.dataset.selectedPlaylistTitle || selectedPlaylistId,
      });
    }

    select.onchange = () => {
      if (select.value === "__create__") {
        select.value = playlistIdInput?.value || "";
        modal?.classList.remove("hidden");
        return;
      }
      updateHiddenPlaylistFields({ select, playlists, playlistIdInput, playlistInfoInput });
      setInlineError(error, "");
    };

    bindPlaylistModalActions({
      container,
      modal,
      select,
      playlists,
      playlistIdInput,
      playlistInfoInput,
      createPlaylistUrl,
    });

    renderPlaylistOptions({ select, playlists, selectedPlaylistId, playlistIdInput, playlistInfoInput });
    loadGroupedYoutubePlaylists({
      container,
      playlistsUrl,
      playlists,
      select,
      selectedPlaylistId,
      playlistIdInput,
      playlistInfoInput,
      loading,
      help,
      error,
    });
  });
}

function initializeGroupedDescriptionEditors(root = document) {
  root.querySelectorAll("[data-grouped-video-description-editor], [data-grouped-youtube-description-editor]").forEach((editorElement) => {
    const descriptionInput = editorElement.hasAttribute("data-grouped-youtube-description-editor")
      ? root.querySelector("[data-grouped-youtube-description-input]")
      : root.querySelector("[data-grouped-video-description-input]");
    const counterElement = editorElement.hasAttribute("data-grouped-youtube-description-editor")
      ? root.querySelector("[data-grouped-youtube-description-counter]")
      : root.querySelector("[data-grouped-video-description-counter]");

    if (!descriptionInput || !window.Quill || editorElement.groupedQuill) return;

    registerAudioDescriptionControls(window.Quill);
    const readOnly = editorElement.dataset?.groupedReadOnly === "true";

    const quill = new window.Quill(editorElement, {
      theme: "snow",
      readOnly,
      placeholder: Object.prototype.hasOwnProperty.call(editorElement.dataset || {}, "placeholder")
        ? editorElement.dataset.placeholder
        : "Your episode description...",
      modules: {
        toolbar: readOnly ? false : {
          container: toolbarOptions,
          handlers: toolbarHandlers(),
        },
        history: {
          delay: 2000,
          maxStack: 500,
          userOnly: true,
        },
        counter: {
          container: counterElement,
        },
      },
    });

    quill.root.innerHTML = descriptionInput.value;
    if (descriptionInput.value) quill.root.classList?.remove("ql-blank");
    updateDescriptionCounter(counterElement, quill.getText());
    quill.on("editor-change", () => {
      descriptionInput.value = quill.root.innerHTML;
      updateDescriptionCounter(counterElement, quill.getText());
    });
    editorElement.groupedQuill = quill;
  });
}

function initializeGroupedKeywordInputs(root = document) {
  root.querySelectorAll("[data-grouped-video-keywords], [data-grouped-youtube-keywords]").forEach((keywordsElement) => {
    if (!window.TomSelect || keywordsElement.tomselect) return;

    new window.TomSelect(keywordsElement, {
      plugins: {
        remove_button: { title: "Remove this tag" },
      },
      persist: false,
      create: true,
      createOnBlur: true,
      render: { no_results: null },
    });
    if (keywordsElement.dataset?.groupedReadOnly === "true") {
      keywordsElement.tomselect.lock();
    }
  });
}

/**
 * Initializes rich description and keyword controls on the grouped video form.
 *
 * @param {Document|HTMLElement} root document or subtree to initialize.
 * @returns {void}
 */
export function initializeGroupedVideoMetadata(root = document) {
  initializeGroupedYoutubePublishing(root);
  initializeGroupedVideoSubmission(root);
  initializeGroupedYoutubePlaylists(root);
  initializeGroupedDescriptionEditors(root);
  initializeGroupedKeywordInputs(root);
  initializeGroupedAudioExtraction(root);
}

/**
 * Starts shared-source audio extraction without depending on a YouTube row.
 *
 * @param {Document|HTMLElement} root document or subtree to initialize.
 * @returns {void}
 */
export function initializeGroupedAudioExtraction(root = document) {
  root.querySelectorAll("[data-grouped-audio-extraction]").forEach((button) => {
    if (button.groupedAudioExtractionInitialized) return;
    button.groupedAudioExtractionInitialized = true;

    button.onclick = async () => {
      const status = root.querySelector("[data-grouped-audio-extraction-status]");
      const originalText = button.textContent;
      button.disabled = true;
      button.textContent = "Extracting audio...";
      status?.classList.add("hidden");

      try {
        const response = await fetch(button.dataset.audioExtractionUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
          },
        });
        const payload = await response.json();
        if (!response.ok || !payload.success) throw new Error(payload.error || "Audio extraction could not be started");

        if (status) {
          status.textContent = "Audio extraction has started. You can continue editing while it finishes.";
          status.classList.remove("hidden");
        }
      } catch (error) {
        button.disabled = false;
        button.textContent = originalText;
        if (status) {
          status.textContent = error.message;
          status.classList.remove("hidden");
          status.classList.remove("text-purple-700");
          status.classList.add("text-red-600");
        }
      }
    };
  });
}

/**
 * Holds Submit and Update Episode while Apple's media check runs, and gives them back after.
 *
 * @param {Document|HTMLElement} root document or subtree with the buttons.
 * @param {string} status the check status shown on the page.
 * @returns {void}
 */
export function applyAppleCheckLock(root, status) {
  const checking = status === "checking";
  root.querySelectorAll("[data-apple-check-lock]").forEach((button) => {
    // a submit is under way: its own lock owns the buttons now
    if (button.closest("[data-grouped-video-form]")?.dataset.submitting === "true") return;

    if (checking) {
      // already held, or disabled for another reason: leave it
      if (button.disabled) return;
      button.disabled = true;
      button.dataset.appleCheckLocked = "";
      button.classList.add("cursor-not-allowed", "opacity-50");
      button.textContent = button.dataset.checkingLabel;
    } else {
      // only give back a button this lock is holding
      if (!("appleCheckLocked" in button.dataset)) return;
      button.disabled = false;
      delete button.dataset.appleCheckLocked;
      button.classList.remove("cursor-not-allowed", "opacity-50");
      button.textContent = button.dataset.label;
    }
  });
}

/**
 * Removes third-party UI wrappers before Turbo caches the page.
 *
 * @param {Document|HTMLElement} root document or subtree to clean.
 * @returns {void}
 */
export function cleanupGroupedVideoMetadata(root = document) {
  root.querySelectorAll("[data-grouped-audio-extraction]").forEach((button) => {
    button.onclick = null;
    delete button.groupedAudioExtractionInitialized;
  });

  root.querySelectorAll("[data-grouped-youtube-publishing] [data-youtube-publishing-action]").forEach((actionSelect) => {
    actionSelect.onchange = null;
  });

  root.querySelectorAll("[data-grouped-youtube-playlists]").forEach((container) => {
    const select = container.querySelector("[data-grouped-youtube-playlist-select]");
    if (select) select.onchange = null;
    const modal = root.querySelector("[data-grouped-youtube-playlist-modal]");
    modal?.querySelectorAll("[data-grouped-youtube-playlist-create], [data-grouped-youtube-playlist-cancel], [data-grouped-youtube-copy-show]").forEach((button) => {
      button.onclick = null;
    });
    delete container.groupedYoutubePlaylistsInitialized;
  });

  root.querySelectorAll("[data-grouped-video-keywords], [data-grouped-youtube-keywords]").forEach((keywordsElement) => {
    if (keywordsElement.tomselect) keywordsElement.tomselect.destroy();
  });

  root.querySelectorAll("[data-grouped-video-description-editor], [data-grouped-youtube-description-editor]").forEach((editorElement) => {
    if (!editorElement.groupedQuill) return;

    const toolbar = editorElement.groupedQuill.getModule("toolbar")?.container;
    toolbar?.remove();
    editorElement.innerHTML = "";
    editorElement.classList.remove("ql-container", "ql-snow");
    delete editorElement.groupedQuill;
  });
}

document.addEventListener("turbo:load", () => initializeGroupedVideoMetadata());
document.addEventListener("DOMContentLoaded", () => initializeGroupedVideoMetadata());
window.addEventListener?.("load", () => initializeGroupedVideoMetadata());
document.addEventListener("turbo:before-cache", () => cleanupGroupedVideoMetadata());
// the uploader says when Apple's media check starts and finishes
document.addEventListener("apple-media-check:status", (event) => applyAppleCheckLock(document, event.detail.status));
